/**
 * InsuranceCardCapture — "Photograph your insurance card" affordance.
 *
 * Placed inside PatientConfirmDetails, in the Insurance section, just above
 * the Plan/Policy and Member ID inputs.  The patient photographs (mobile) or
 * uploads (desktop) their insurance card; the image is:
 *
 *   1. Downscaled client-side (reuses letterUpload.prepareFileForUpload).
 *   2. Sealed under the Mac's X25519 public key (same sealed-box as letters).
 *   3. Uploaded to the bridge as doc_kind: "insurance_card".
 *
 * IMMEDIATE UX after upload:
 *   "We'll read the card — please still check the numbers below."
 *   The flow is NOT blocked; the patient continues typing/confirming.
 *   OCR runs async on the Mac side; physician sees results in the chip.
 *
 * Auth: requires an active bridge session (same session the magic-link
 * flow established).  When no session is available the affordance is hidden
 * (the patient already left the portal or expired their session).
 *
 * States:
 *   IDLE       — "Photograph your insurance card" button visible.
 *   PREPARING  — downscaling the image.
 *   UPLOADING  — network request in flight.
 *   SUCCESS    — warm confirmation + "numbers below" note; still allows re-scan.
 *   ERROR      — error banner; retry available.
 */

import React, { useEffect, useRef, useState } from "react";
import { getBridgeSession } from "../../lib/bridgeClient";
import { sealAndUploadInsuranceCard } from "../../lib/insuranceCardUpload";
import { extractCardFields } from "../../lib/cardOcrLocal";

// Phones get the native capture flow (file input + capture="environment"
// opens the rear camera directly). Desktops ignore `capture` and show a file
// dialog — so there we open a getUserMedia webcam modal instead. Email
// invitations are routinely opened on computers, so this path is real, not
// an edge case. iPads masquerade as Macintosh but report touch points.
const IS_MOBILE =
  typeof navigator !== "undefined" &&
  (/Mobi|Android|iPhone|iPod/i.test(navigator.userAgent) ||
    (/iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

const STATE = {
  IDLE: "idle",
  PREPARING: "preparing",
  UPLOADING: "uploading",
  SUCCESS: "success",
  ERROR: "error",
};

const C = {
  navy: "#182a33",
  sage: "#6b9c8e",
  sageBg: "#eef4f2",
  sageBorder: "#bbd5cc",
  tan: "#d8c9b6",
  cream: "#fdf9f5",
  border: "#e6dccb",
  muted: "#5b6e7c",
  white: "#ffffff",
  errorBg: "#faeeed",
  errorBorder: "#f3d5d3",
  errorText: "#c25450",
};

// Accept images only — we're doing OCR, not PDF parsing.
const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp,image/heic,image/heif";

interface InsuranceCardCaptureProps {
  /** Pre-checked by parent; avoids redundant sessionStorage reads on every
   *  render. Defaults to checking getBridgeSession() internally when omitted. */
  sessionAvailable?: boolean;
  /** Applies OCR candidates to the form; returns the count applied (or void). */
  onExtracted?: (candidates: any[]) => number | void;
}

export default function InsuranceCardCapture({ sessionAvailable, onExtracted }: InsuranceCardCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<string>(STATE.IDLE);
  const [errorMsg, setErrorMsg] = useState("");
  const [showWebcam, setShowWebcam] = useState(false);
  const [filledCount, setFilledCount] = useState(0);

  // Check session availability (only once per mount if not passed as prop).
  const hasSession =
    sessionAvailable !== undefined
      ? sessionAvailable
      : Boolean(getBridgeSession()?.sessionToken);

  // If there's no live session the patient can't upload; hide the affordance
  // silently (they still see the text inputs below).
  if (!hasSession) return null;

  async function handleFile(file: File) {
    setErrorMsg("");
    setFilledCount(0);
    setUploadState(STATE.PREPARING);

    // On-device OCR runs in parallel with the sealed upload: the patient
    // sees their numbers appear in the boxes below within seconds, while the
    // encrypted image still reaches the physician for verification chips.
    const ocrPromise = onExtracted
      ? extractCardFields(file).then(({ candidates }: { candidates: any[] }) => {
          if (candidates.length) {
            const applied = onExtracted(candidates);
            setFilledCount(typeof applied === "number" ? applied : candidates.length);
          }
        })
      : Promise.resolve();

    try {
      setUploadState(STATE.UPLOADING);
      await sealAndUploadInsuranceCard(file);
      await ocrPromise;
      setUploadState(STATE.SUCCESS);
    } catch (err) {
      setErrorMsg((err as Error).message || "Upload failed. Please try again.");
      setUploadState(STATE.ERROR);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-selecting the same file after error
    handleFile(file);
  }

  function startScan() {
    if (!IS_MOBILE && (navigator.mediaDevices as any)?.getUserMedia) {
      setShowWebcam(true);
    } else {
      inputRef.current?.click();
    }
  }

  const isBusy = uploadState === STATE.PREPARING || uploadState === STATE.UPLOADING;

  // ── render helpers ──────────────────────────────────────────────────────

  if (uploadState === STATE.SUCCESS) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: C.sageBg,
            border: `1px solid ${C.sageBorder}`,
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle />
            <span style={{ fontSize: 14, fontWeight: 600, color: "#2c5246" }}>
              {filledCount > 0
                ? "Card read — we've filled in what we found below."
                : "Card received — we'll read the numbers for you."}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Please check the plan and member numbers below — card reading isn't
            perfect, and your doctor will verify everything before submitting.
          </p>
          {/* Allow re-scan without reloading */}
          <button
            type="button"
            onClick={() => {
              setUploadState(STATE.IDLE);
              setErrorMsg("");
            }}
            style={{
              alignSelf: "flex-start",
              marginTop: 2,
              padding: "4px 12px",
              fontSize: 12,
              fontWeight: 600,
              backgroundColor: "transparent",
              color: C.sage,
              border: `1px solid ${C.sageBorder}`,
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Re-scan card
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Hidden file input — capture="environment" opens rear camera on mobile. */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
        aria-label="Photograph or upload your insurance card"
      />

      {uploadState === STATE.ERROR && errorMsg && (
        <div
          style={{
            marginBottom: 8,
            padding: "8px 12px",
            backgroundColor: C.errorBg,
            border: `1px solid ${C.errorBorder}`,
            borderRadius: 6,
            fontSize: 13,
            color: C.errorText,
          }}
        >
          {errorMsg}
        </div>
      )}

      <div
        style={{
          padding: "12px 16px",
          backgroundColor: C.cream,
          border: `1.5px dashed ${C.tan}`,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        {/* Camera icon */}
        <span
          aria-hidden="true"
          style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}
        >
          📷
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
            Photograph your insurance card
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.muted,
              marginTop: 2,
              lineHeight: 1.45,
            }}
          >
            We'll pre-fill the plan and member numbers — you'll confirm them
            below before continuing.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => !isBusy && startScan()}
            disabled={isBusy}
            aria-busy={isBusy}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: isBusy ? C.tan : C.navy,
              color: isBusy ? C.muted : C.white,
              border: "none",
              borderRadius: 8,
              cursor: isBusy ? "default" : "pointer",
              whiteSpace: "nowrap",
              transition: "background-color 0.15s",
            }}
          >
            {uploadState === STATE.PREPARING && "Preparing..."}
            {uploadState === STATE.UPLOADING && "Uploading..."}
            {(uploadState === STATE.IDLE || uploadState === STATE.ERROR) &&
              (IS_MOBILE ? "Scan card" : "Take photo")}
          </button>
          {/* Desktop second affordance: pick an existing photo/scan file. */}
          {!IS_MOBILE && (uploadState === STATE.IDLE || uploadState === STATE.ERROR) && (
            <button
              type="button"
              onClick={() => !isBusy && inputRef.current?.click()}
              disabled={isBusy}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: "transparent",
                color: C.navy,
                border: `1px solid ${C.tan}`,
                borderRadius: 8,
                cursor: isBusy ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Upload
            </button>
          )}
        </div>
      </div>

      {showWebcam && (
        <WebcamCaptureModal
          onCapture={(file) => {
            setShowWebcam(false);
            handleFile(file);
          }}
          onUploadInstead={() => {
            setShowWebcam(false);
            inputRef.current?.click();
          }}
          onClose={() => setShowWebcam(false)}
        />
      )}
    </div>
  );
}

/**
 * Desktop webcam capture: getUserMedia preview + a shutter button that
 * draws the current frame to a canvas and hands back a JPEG File. The
 * stream is stopped on capture/cancel/unmount so the camera light goes off.
 */
interface WebcamCaptureModalProps {
  onCapture: (file: File) => void;
  onUploadInstead: () => void;
  onClose: () => void;
}

function WebcamCaptureModal({ onCapture, onUploadInstead, onClose }: WebcamCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [camError, setCamError] = useState("");

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => {
        if (!cancelled)
          setCamError(
            "Couldn't access a camera on this device. You can upload a photo of your card instead."
          );
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function snap() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(new File([blob], "insurance-card.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  }

  const btnStyle = (primary: boolean): React.CSSProperties => ({
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: primary ? C.navy : "transparent",
    color: primary ? C.white : C.navy,
    border: primary ? "none" : `1px solid ${C.tan}`,
    borderRadius: 8,
    cursor: "pointer",
  });

  return (
    <div
      role="dialog"
      aria-label="Take a photo of your insurance card"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        backgroundColor: "rgba(24,42,51,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          backgroundColor: C.white,
          borderRadius: 12,
          padding: 16,
          width: "100%",
          maxWidth: 560,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
          Hold your insurance card up to the camera
        </div>
        {camError ? (
          <div
            style={{
              padding: "10px 12px",
              backgroundColor: C.errorBg,
              border: `1px solid ${C.errorBorder}`,
              borderRadius: 8,
              fontSize: 13,
              color: C.errorText,
            }}
          >
            {camError}
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              borderRadius: 8,
              backgroundColor: "#0b151a",
              aspectRatio: "4 / 3",
              objectFit: "cover",
            }}
          />
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" onClick={onClose} style={btnStyle(false)}>
            Cancel
          </button>
          <button type="button" onClick={onUploadInstead} style={btnStyle(false)}>
            Upload a photo instead
          </button>
          {!camError && (
            <button type="button" onClick={snap} style={btnStyle(true)}>
              Take photo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckCircle() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" fill="#6b9c8e" />
      <path
        d="M7 12.5l3.2 3.2L17 9"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
